<template>
  <b-card>
    <b-form v-if="render">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="page-category-title"
            class="mb-2"
          >
            <b-form-input
              id="page-category-title"
              v-model="form.title"
              :state="validateState('title')"
              aria-describedby="input-title-feedback"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="page-category-slug"
            class="mb-2"
          >
            <b-form-input
              id="page-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
              aria-describedby="input-slug-feedback"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('pageCategories.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="page-category-language"
            class="mb-2"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="page-category-keywords"
            class="mb-2"
          >
            <b-form-input
              id="page-category-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
              aria-describedby="input-keywords-feedback"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="page-lang"
            class="mb-2"
          >
            <b-form-textarea
              id="page-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} -
              {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageCatThumbnail"
              v-model="form.pageCatThumbnail"
              :state="validateState('pageCatThumbnail')"
              :alt.sync="form.pageCatThumbnailAlt"
              :alt-validator="$v.form.pageCatThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageCatThumbnail-feedback"
                  :label="$t('pageCategories.pageCatThumbnail')"
                  :validator="$v.form.pageCatThumbnail"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageCatCoverImage"
              v-model="form.pageCatCoverImage"
              :state="validateState('pageCatCoverImage')"
              :alt.sync="form.pageCatCoverImageAlt"
              :alt-validator="$v.form.pageCatCoverImageAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageCatCoverImage-feedback"
                  :label="$t('pageCategories.pageCatCoverImage')"
                  :validator="$v.form.pageCatCoverImage"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.update") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import Ripple from 'vue-ripple-directive'
import languageSelector from '@/components/language-selector.vue'
import 'vue-select'
import { required, minLength } from 'vuelidate/lib/validators'
import usePageCategoriesList from './usePageCategoriesList'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        description: {
          required,
          minLength: minLength(10),
        },
        pageCatThumbnail: {
          required,
        },
        pageCatCoverImage: {
          required,
        },
        pageCatThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
        pageCatCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  setup() {
    const {
      addItem,
      changeLanguage,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      updateItem,
      convertResource,
    } = usePageCategoriesList()
    return {
      addItem,
      changeLanguage,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      updateItem,
      convertResource,
    }
  },
  computed: {
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  created() {
    this.fetchItem(this.$router.currentRoute.params.id)
  },
  methods: {
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(this.item, selectedLanguage)
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['pageCatCoverImage', 'pageCatThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData() {
      const data = {
        title: this.form.title,
        slug: this.form.slug,
        keywords: this.form.keywords,
        description: this.form.description,
        pageCatCoverImageFileId: this.form.pageCatCoverImage ? this.form.pageCatCoverImage._id : null,
        pageCatThumbnailFileId: this.form.pageCatThumbnail ? this.form.pageCatThumbnail._id : null,
      }
      this.updateItem(this.$router.currentRoute.params.id, data, this.form.lang)
    },
  },
}
</script>
